import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { SettingKey, CreateSettingKeyRequest, UpdateSettingKeyRequest, SettingScope } from '../models/setting-key.model';
import { ApiResponse } from '../interfaces/api.interface';

@Injectable({
  providedIn: 'root'
})
export class SettingKeyService {
  private baseUrl = '/api/company/setting-keys';

  constructor(private http: HttpClient) {}

  /**
   * Get all setting keys
   */
  getAll(): Observable<ApiResponse<SettingKey[]>> {
    return this.http.get<ApiResponse<SettingKey[]>>(this.baseUrl);
  }

  /**
   * Get setting keys by scope
   */
  getByScope(scope: SettingScope | string): Observable<ApiResponse<SettingKey[]>> {
    return this.http.get<ApiResponse<SettingKey[]>>(`${this.baseUrl}/scope/${scope}`);
  }

  /**
   * Get setting keys for current company type (no admin permission needed).
   * Backend auto-detects scope: corporate -> corporate_company, fleet -> fleet_company, else -> company.
   */
  getMyScope(): Observable<ApiResponse<SettingKey[]>> {
    return this.http.get<ApiResponse<SettingKey[]>>(`${this.baseUrl}/my-scope`);
  }

  /**
   * Get setting keys by group
   */
  getByGroup(groupName: string): Observable<ApiResponse<SettingKey[]>> {
    return this.http.get<ApiResponse<SettingKey[]>>(`${this.baseUrl}/group/${groupName}`);
  }

  /**
   * Get all setting keys grouped by category
   */
  getGrouped(): Observable<ApiResponse<{ [group: string]: SettingKey[] }>> {
    return this.http.get<ApiResponse<{ [group: string]: SettingKey[] }>>(`${this.baseUrl}/grouped`);
  }

  /**
   * Get setting key by slug
   */
  getBySlug(slug: string): Observable<ApiResponse<SettingKey>> {
    return this.http.get<ApiResponse<SettingKey>>(`${this.baseUrl}/slug/${slug}`);
  }

  /**
   * Get setting key by ID
   */
  getById(id: number): Observable<ApiResponse<SettingKey>> {
    return this.http.get<ApiResponse<SettingKey>>(`${this.baseUrl}/${id}`);
  }

  /**
   * Create new setting key
   */
  create(data: CreateSettingKeyRequest): Observable<ApiResponse<SettingKey>> {
    return this.http.post<ApiResponse<SettingKey>>(this.baseUrl, data);
  }

  /**
   * Update setting key
   */
  update(id: number, data: UpdateSettingKeyRequest): Observable<ApiResponse<SettingKey>> {
    return this.http.put<ApiResponse<SettingKey>>(`${this.baseUrl}/${id}`, data);
  }

  /**
   * Toggle setting key active status
   */
  toggleActive(id: number): Observable<ApiResponse<SettingKey>> {
    return this.http.put<ApiResponse<SettingKey>>(`${this.baseUrl}/${id}/toggle-active`, {});
  }

  /**
   * Delete setting key
   */
  delete(id: number): Observable<ApiResponse<{ message: string }>> {
    return this.http.delete<ApiResponse<{ message: string }>>(`${this.baseUrl}/${id}`);
  }
}
